import { useMemo, useState } from 'react';
import estilos from './Inicio.module.css';

const initialForm = {
  origin: '',
  destination: '',
  departure: '',
  returnDate: '',
  passengers: '1',
};

const popularDestinations = [
  { city: 'Londres', country: 'Reino Unido' },
  { city: 'Lisboa', country: 'Portugal' },
  { city: 'Miami', country: 'Estados Unidos' },
];

const features = [
  {
    title: 'Resultados inteligentes',
    description: 'Compara preços, horários e escalas em uma interface leve e rápida.',
  },
  {
    title: 'Recomendações personalizadas',
    description: 'Sugerimos rotas com base em melhor custo-benefício e tempo de viagem.',
  },
  {
    title: 'Layout seguro e leve',
    description: 'Plataforma desenhada para conversão e experiência profissional.',
  },
];

function formatCurrency(value) {
  return Number(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
}

function formatDate(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value));
}

function normalizeDuration(duration) {
  return duration.replace('PT', '').replace('H', 'h ').replace('M', 'm');
}

function createOffers(form) {
  if (!form.origin || !form.destination || !form.departure) {
    return [];
  }

  const route = `${form.origin.toUpperCase()} → ${form.destination.toUpperCase()}`;
  const basePrice = 1199 + form.origin.length * 12 + form.destination.length * 8;
  const passengerCount = Number(form.passengers) || 1;

  return [
    {
      id: 'mock-1',
      airline: 'AeroSense',
      label: 'Melhor preço',
      route,
      departure: `${formatDate(form.departure)} • ${form.passengers} passageiro(s)`,
      duration: '10h 30m',
      stops: '1 escala',
      price: Math.round(basePrice * 1.05 * passengerCount),
      badge: 'Econômico',
    },
    {
      id: 'mock-2',
      airline: 'SkyWays',
      label: 'Menor duração',
      route,
      departure: `${formatDate(form.departure)} • ${form.passengers} passageiro(s)`,
      duration: '8h 05m',
      stops: 'Direto',
      price: Math.round(basePrice * 1.32 * passengerCount),
      badge: 'Rápido',
    },
    {
      id: 'mock-3',
      airline: 'ViaAzure',
      label: 'Recomendado',
      route,
      departure: `${formatDate(form.departure)} • ${form.passengers} passageiro(s)`,
      duration: '9h 15m',
      stops: '1 escala',
      price: Math.round(basePrice * 1.18 * passengerCount),
      badge: 'Mais vendido',
    },
  ];
}

export default function Inicio() {
  const [form, setForm] = useState(initialForm);
  const [offers, setOffers] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const results = useMemo(() => (searched ? offers : []), [searched, offers]);

  const handleChange = ({ target }) => {
    setForm((previous) => ({ ...previous, [target.name]: target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSearched(true);
    setLoading(true);
    setError('');

    const origin = form.origin.trim().toUpperCase();
    const destination = form.destination.trim().toUpperCase();
    const departureDate = form.departure;

    if (!origin || !destination || !departureDate) {
      setError('Preencha origem, destino e data de ida antes de buscar.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `/api/flight-search?origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}&departureDate=${encodeURIComponent(departureDate)}&returnDate=${encodeURIComponent(form.returnDate)}&passengers=${encodeURIComponent(form.passengers)}`
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao buscar voos.');
      }

      setOffers(data.offers || []);
    } catch (exception) {
      setError(
        exception.message || 'Não foi possível buscar as ofertas no momento. Verifique a configuração da API e tente novamente.'
      );
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className={estilos.page}>
      <section className={estilos.hero}>
        <div className={estilos.heroTexto}>
          <span className={estilos.badge}>Seu buscador de voos moderno</span>
          <h1>Encontre passagens aéreas com o melhor preço e a rota ideal</h1>
          <p>
            Informe origem, destino e datas. Nós mostramos em segundos as
            opções mais recomendadas para sua viagem.
          </p>
          <div className={estilos.heroCards}>
            <div>
              <strong>+ 120 destinos</strong>
              <p>decolar com confiança</p>
            </div>
            <div>
              <strong>Consistência</strong>
              <p>preços atualizados e comparados</p>
            </div>
          </div>
        </div>
        <div className={estilos.heroVisual}>
          <div className={estilos.heroVisualCard}>
            <span>Ofertas do dia</span>
            <strong>R$ 1.430</strong>
            <p>GRU → LIS • 1 parada • 10h 30m</p>
          </div>
        </div>
      </section>

      <section id="busca" className={estilos.buscaBloco}>
        <div className={estilos.buscaPainel}>
          <h2>Busque sua próxima viagem</h2>
          <p>Sem cadastro. Sem complicação. Apenas resultados claros e acessíveis.</p>
          <form className={estilos.form} onSubmit={handleSubmit}>
            <label>
              Origem
              <input
                name="origin"
                value={form.origin}
                onChange={handleChange}
                placeholder="Ex: GRU"
                required
              />
            </label>
            <label>
              Destino
              <input
                name="destination"
                value={form.destination}
                onChange={handleChange}
                placeholder="Ex: LIS"
                required
              />
            </label>
            <label>
              Ida
              <input
                type="date"
                name="departure"
                value={form.departure}
                onChange={handleChange}
                required
              />
            </label>
            <label>
              Volta
              <input
                type="date"
                name="returnDate"
                value={form.returnDate}
                onChange={handleChange}
              />
            </label>
            <label>
              Passageiros
              <select name="passengers" value={form.passengers} onChange={handleChange}>
                <option value="1">1 passageiro</option>
                <option value="2">2 passageiros</option>
                <option value="3">3 passageiros</option>
                <option value="4">4 passageiros</option>
              </select>
            </label>
            <button type="submit" disabled={loading}>
              {loading ? 'Buscando...' : 'Buscar voos'}
            </button>
          </form>
          {error && <p className={estilos.errorMessage}>{error}</p>}
        </div>
        <div className={estilos.populares}>
          <h3>Roteiros populares</h3>
          <div className={estilos.popularesGrid}>
            {popularDestinations.map((destination) => (
              <div key={destination.city} className={estilos.popularCard}>
                <strong>{destination.city}</strong>
                <p>{destination.country}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="ofertas" className={estilos.resultadosBloco}>
        <div className={estilos.sectionHeader}>
          <h2>
            {searched
              ? `Resultados para ${form.origin.toUpperCase()} → ${form.destination.toUpperCase()}`
              : 'Ofertas em destaque'}
          </h2>
          <p>
            {searched
              ? 'Veja as melhores opções filtradas para sua busca.'
              : 'Sugestões de rotas em promoção para você começar a viajar.'}
          </p>
        </div>

        <div className={estilos.cardsGrid}>
          {(searched ? results : createOffers({ origin: 'GRU', destination: 'LIS', departure: '2026-08-01', returnDate: '', passengers: '1' })).map((offer) => (
            <article key={offer.id} className={estilos.card}>
              <div className={estilos.cardTop}>
                <span className={estilos.cardBadge}>{offer.badge}</span>
                <p>{offer.airline}</p>
              </div>
              <h3>{offer.route}</h3>
              <p className={estilos.cardMeta}>{offer.departure}</p>
              <div className={estilos.cardRow}>
                <span>{offer.duration}</span>
                <span>{offer.stops}</span>
              </div>
              <div className={estilos.cardFooter}>
                <strong>{formatCurrency(offer.price)}</strong>
                <button type="button">Ver detalhes</button>
              </div>
            </article>
          ))}
          {searched && results.length === 0 && !loading && (
            <div className={estilos.emptyState}>
              <p>Nenhuma oferta disponível para esses parâmetros. Tente outro destino ou data.</p>
            </div>
          )}
        </div>
      </section>

      <section id="vantagens" className={estilos.vantagensBloco}>
        <div className={estilos.sectionHeader}>
          <h2>Por que escolher SkySense</h2>
          <p>Uma experiência pensada para quem quer viajar com melhor custo-benefício.</p>
        </div>
        <div className={estilos.vantagensGrid}>
          {features.map((feature) => (
            <div key={feature.title} className={estilos.featureCard}>
              <strong>{feature.title}</strong>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="contato" className={estilos.ctaBloco}>
        <div>
          <h2>Pronto para começar?</h2>
          <p>Crie seu portal de viagens ou vendemos o acesso premium no futuro.</p>
        </div>
        <a href="#busca">Realizar busca</a>
      </section>
    </main>
  );
}
